var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';
import css from '@styled-system/css';
const TabList = styled.div.attrs({
    role: 'tablist',
})({
    display: 'flex',
    padding: 0,
    position: 'relative',
}, variant({
    prop: 'align',
    variants: {
        left: { justifyContent: 'flex-start' },
        right: { justifyContent: 'flex-end' },
        center: { justifyContent: 'center' },
        even: { justifyContent: 'space-evenly' },
    },
}), ({ theme: { colors } }) => variant({
    prop: 'variation',
    variants: {
        underline: {
            borderBottom: `1px solid ${colors.surfaceDividerLight}`,
            padding: '0 20px',
        },
        underlineSmall: {
            borderBottom: `1px solid ${colors.surfaceDividerLight}`,
            padding: '0 0px',
        },
        pill: {
            border: `1px solid ${colors.inputBorder}`,
            borderRadius: '20px',
            display: 'inline-flex',
        },
        squared: {
            borderRadius: 'xl',
        },
        default: { display: 'inline-block' },
    },
}), (props) => 
// It overwrites the property of variation as well as the default CSS.
css(Object.assign({}, props.tabListStyles)));
const Tab = styled.button.attrs({
    role: 'tab',
    className: 'tab-button',
})((props) => css({
    backgroundColor: props['aria-selected'] === 'true' && props.variation === 'overline' ? 'bg.blueGray' : 'bg.transparent',
    border: 0,
    color: props['aria-selected'] === 'true' && props.variation === 'pill' ? 'text.white' : 'text.black',
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    fontWeight: props['aria-selected'] === 'true' ? 'semiBold' : 'regular',
    margin: '0 4px',
    outline: 0,
    position: 'relative',
    transition: 'color 250ms ease-in',
    ':focus': {
        fontWeight: 'semiBold',
        borderBottomColor: props.theme.colors.surfaceDividerStrong,
    },
}), (props) => {
    const { theme: { colors }, } = props;
    return variant({
        prop: 'variation',
        variants: {
            underline: {
                borderBottom: '3px solid transparent',
                margin: '0 20px',
                padding: '32px 0px',
                '&:hover': {
                    borderBottomColor: colors.surfaceDividerStrong,
                },
            },
            underlineSmall: {
                borderBottom: '3px solid transparent',
                margin: '0 10px',
                padding: '24px 0',
                '&:hover': {
                    borderBottomColor: colors.surfaceDividerStrong,
                },
            },
            pill: {
                borderRadius: '16px',
                padding: '10px 20px',
                '&:hover': {
                    fontWeight: 'semiBold',
                },
            },
            squared: {
                width: 64,
                height: 64,
                border: 0,
                borderRadius: 'sm',
                margin: '0',
                marginRight: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
            },
            squaredSmall: {
                width: 50,
                height: 50,
                border: 0,
                borderRadius: 'sm',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                position: 'relative',
                '&:hover': {
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: props['aria-selected'] !== 'true' ? 'bg.blueGray' : 'bg.transparent',
                },
            },
            overline: {
                fontSize: 16,
                borderTop: 'thinSolid',
                borderTopColor: 'border.regular',
                '&:hover': {
                    borderTopColor: 'border.disabled',
                },
                margin: '0',
                borderRight: 'thinSolid',
                borderRightColor: 'border.regular',
                borderLeft: 'thinSolid',
                borderLeftColor: 'border.regular',
                '&:first-of-type': {
                    borderRadius: '8px 0 0 0',
                },
                '&:last-child': {
                    borderRadius: '0 8px 0 0',
                },
            },
            default: {},
        },
    });
}, (props) => 
// It overwrites the property of variation as well as the default CSS.
css(Object.assign({}, props.buttonStyles)));
const TabActionWrapper = styled.div({
    position: 'absolute',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
}, variant({
    prop: 'align',
    variants: {
        left: { left: 0 },
        right: { right: 0 },
    },
}));
const ActiveTabIndicator = styled.div((props) => ({
    backgroundColor: props.theme.colors.primary,
    position: 'absolute',
    width: '0px',
    transition: 'all 250ms ease-in-out',
}), variant({
    prop: 'variation',
    variants: {
        underline: {
            bottom: 0,
            height: '3px',
            zIndex: 1,
        },
        underlineSmall: {
            bottom: 0,
            height: '3px',
            zIndex: 1,
        },
        pill: {
            top: '4px',
            bottom: '4px',
            borderRadius: '16px',
        },
        squared: css({
            width: 64,
            height: 64,
            backgroundColor: 'bg.blueGray',
            border: 'thickSolid',
            borderColor: 'border.regular',
            borderRadius: 'sm',
            top: 0,
            left: 0,
        }),
        squaredSmall: css({
            width: 48,
            height: 48,
            backgroundColor: 'bg.regular',
            border: 'thinSolid',
            borderColor: 'border.regular',
            borderRadius: 'sm',
            top: 0,
            left: 0,
        }),
        overline: {
            top: '-2px',
            height: '4px',
            zIndex: 1,
        },
        default: {},
    },
}));
/**
 * Tabs component tries to follow WAI-ARIA Authoring Practices for
 * [Tabs with Automatic Activation](https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html)
 */
const HorizontalTabs = (_a) => {
    var { 'aria-label': ariaLabel, tabLabels, onChange, align, selectedKey, variation, dataTestId, buttonStyles, tabListStyles, leftAction, rightAction, shouldScrollTabItemsIntoView, useRightActionContainer = true, disabledLabels, buttonProps } = _a, otherProps = __rest(_a, ['aria-label', "tabLabels", "onChange", "align", "selectedKey", "variation", "dataTestId", "buttonStyles", "tabListStyles", "leftAction", "rightAction", "shouldScrollTabItemsIntoView", "useRightActionContainer", "disabledLabels", "buttonProps"]);
    const tabs = Object.entries(tabLabels);
    const noOfTabs = tabs.length;
    const [selectedTab, setSelectedTab] = useState(selectedKey);
    const tabKeys = Object.keys(tabLabels);
    const tabListRef = useRef(null);
    const tabsRef = useRef({});
    const changeTab = (key) => {
        setSelectedTab(key);
        if (onChange)
            onChange(key);
        if (shouldScrollTabItemsIntoView) {
            const containerElement = tabListRef === null || tabListRef === void 0 ? void 0 : tabListRef.current;
            const currentTab = tabsRef.current[key];
            if (!containerElement || !currentTab) {
                return;
            }
            const leftMostPixelOfTabItem = currentTab.offsetLeft;
            const currentLeftMostPixelOfParentInView = containerElement.scrollLeft;
            const tabItemWidth = currentTab.offsetWidth;
            const parentItemWidth = containerElement.offsetWidth;
            // Checking if the current element is within the viewport
            if (leftMostPixelOfTabItem > currentLeftMostPixelOfParentInView &&
                leftMostPixelOfTabItem + tabItemWidth < currentLeftMostPixelOfParentInView + parentItemWidth) {
                return;
            }
            const scrollLeft = leftMostPixelOfTabItem + tabItemWidth / 2 - parentItemWidth / 2;
            containerElement.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        }
    };
    const indicatorRef = useRef(null);
    useEffect(() => {
        var _a, _b;
        if (indicatorRef.current !== null && variation !== 'default') {
            indicatorRef.current.style.width = `${(_a = tabsRef.current[selectedTab]) === null || _a === void 0 ? void 0 : _a.offsetWidth}px`;
            indicatorRef.current.style.left = `${(_b = tabsRef.current[selectedTab]) === null || _b === void 0 ? void 0 : _b.offsetLeft}px`;
        }
    });
    useEffect(() => {
        setSelectedTab(selectedKey);
    }, [selectedKey]);
    const handleClick = (key) => () => {
        if (disabledLabels === null || disabledLabels === void 0 ? void 0 : disabledLabels.includes(key)) {
            return;
        }
        changeTab(key);
    };
    const handleKeyDown = ({ key }) => {
        switch (key) {
            case 'ArrowRight':
                changeTab(selectedTab);
                break;
            case 'ArrowLeft':
                changeTab(selectedTab);
                break;
            case 'Home':
                changeTab(tabKeys[0]);
                break;
            case 'End':
                changeTab(tabKeys[noOfTabs - 1]);
                break;
            default:
                break;
        }
    };
    return (React.createElement(TabList, Object.assign({ align: align, variation: variation, "aria-label": ariaLabel, "data-testid": dataTestId, tabListStyles: tabListStyles, ref: tabListRef }, otherProps),
        leftAction && React.createElement(TabActionWrapper, { align: "left" }, leftAction),
        React.createElement(ActiveTabIndicator, { ref: indicatorRef, variation: variation }),
        tabs.map(([id, Label]) => (React.createElement(Tab, { key: id, variation: variation, onClick: handleClick(id), onKeyDown: handleKeyDown, ref: (el) => {
                tabsRef.current[id] = el;
            }, "aria-selected": selectedTab === id ? 'true' : 'false', "aria-controls": id, "data-testid": dataTestId + id, buttonStyles: buttonStyles, disabled: disabledLabels === null || disabledLabels === void 0 ? void 0 : disabledLabels.includes(id) },
            React.createElement(Label, { isActive: selectedTab === id })))),
        rightAction && useRightActionContainer && React.createElement(TabActionWrapper, { align: "right" }, rightAction),
        rightAction && !useRightActionContainer && rightAction));
};
HorizontalTabs.defaultProps = {
    align: 'left',
    variation: 'underline',
};
export default HorizontalTabs;
